import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap, map } from 'rxjs';
import { HttpService } from '../http/http.service';
import { HttpHeaders } from '@angular/common/http';
import { UtilitiesService } from '../utilities/utilities.service';
import { 
  IApiGeneralResponse,
  ICancelServiceJob,
  IClaimDetailsServiceJob,
  IServiceJob,
} from '../../components/pages/claim-history/claim-history.interface';
import {
  GET_CONTRACTS,
  getApiUrlForParams,
  PAST_DUE_PAYMENT_API,
  VIVR_SERVICEJOBS_LIST,
  VIVR_SERVICEJOBID_DETAILS,
  VIVR_SERVICEJOBID_DETAILS_CANCEL,
  PM_ITEMS,
  FIND_PLAN_API_URL
} from '../../utils/api.util';
import { IContractData } from '../../interfaces/IJob';
import { parseJwt } from '../../utils/helper';
import { IPastDuePaymentResponse, IPayloadForPastDuePayment } from '../../interfaces/IPayments';
import { IPMItem } from '../../components/pages/service-request/service-request.interface';
import { ISearchPlanReqBodyByContract } from '../../interfaces/ISearchPlan';
import { ISearchContractResponse } from '../../interfaces/ISearchContract';
import { DataService } from '../data/data.service';
import { IEmailNotificationPayload, IEmailTemplateVars } from '../../interfaces/IEmailNotification';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class VivrService {
  claimsData$ = new BehaviorSubject<IServiceJob[] | null>(null);
  claim$ = new BehaviorSubject<IServiceJob | null>(null);
  private get httpOptions() {
    return {
      headers: new HttpHeaders(this.httpService.getHeaders(this.dataService.vivrAccessToken$.value)),
    };
  }

  constructor(
    private httpService: HttpService,
    private dataService: DataService,
    private utilService: UtilitiesService
  ) {
  }

  

  getContractData(subStr: string, contractNumber: string): Observable<IContractData | null> {
    const url = getApiUrlForParams(GET_CONTRACTS, { subStr, contractNumber });
    return this.httpService.get<IContractData | null>(url, this.httpOptions).pipe(
      tap((contractData) => {
        this.dataService.contractData$.next(contractData);
        this.dataService.vivrSource$.next(subStr);
      })
    );
  }

    /**
     * Sends an email notification using dynamic 'to' and 'templateVars', other fields are static.
     * @param to Recipient email address
     * @param templateVars Dynamic template variables
     */
    sendEmailNotification(templateVars: IEmailTemplateVars) {
      // Use config for recipient and subject prefix
      const to: string = environment.emailRecipient || '';
      // Compose subject using only whitelisted/config values
      const subject = `${environment.emailSubjectPrefix}`;
      const payload: IEmailNotificationPayload = {
        from: 'noreply@cinchhs.com',
        to,
        subject,
        template: 'retention_dtc_ce',
        templateVars,
        referenceIDsMap: null,
        templateSource: 'liferay',
        templateSourceId: '1124825'
      };
      return this.httpService.post<IEmailNotificationPayload>(`api/user/emailNotification`, payload, this.httpOptions);
    }

  findContract(reqObj: ISearchPlanReqBodyByContract): Observable<ISearchContractResponse[]> {
    const options = {
      ...this.httpOptions
    };
    return this.httpService.post<ISearchContractResponse[]>(FIND_PLAN_API_URL, reqObj, options);
  }

  /**
   * @description Fetches claims data from the API and updates the claimsData$ observable.
   * @param subStr 
   * @param contractNumber 
   * @returns 
   */
  getClaimsData(subStr: string, contractNumber: string): Observable<IServiceJob[]> {
    const url = getApiUrlForParams(VIVR_SERVICEJOBS_LIST, {subStr, contractNumber});
    return this.httpService.get<IServiceJob[]>(url, this.httpOptions).pipe(
      map((response) => {
        const jobs = response ?? [];
        const contractData = this.dataService.contractData$.getValue();
        if (contractData) {
          const isPlanBYOP = this.utilService.isPlanBYOP(contractData.baseContract.coverageGroup);
          const isPlanSurge = this.utilService.isPlanSurge(contractData.baseContract.coverageGroup);
          jobs.forEach(job => {
            job.showCancelRequestButtonUI = this.utilService.getShowCancelRequestButtonUI(job, isPlanBYOP, isPlanSurge);
            job.showServiceAgainButtonUI = this.utilService.getShowServiceAgainButtonUI(job, isPlanBYOP, isPlanSurge);
          });
        }
        return jobs;
      }),
      tap((claimsData) => this.claimsData$.next(claimsData))
    );
  }

  processAccessToken(token: string): any {
    return parseJwt(token);
  }

  // past due payment api
  paymentForPastDue(data: IPayloadForPastDuePayment): Observable<IPastDuePaymentResponse | null> {
    if (!data.contractNumber) { return of(null); }
    const url = getApiUrlForParams(PAST_DUE_PAYMENT_API, {contractNumber: data.contractNumber});
    return this.httpService.post<IPastDuePaymentResponse>(url, data, this.httpOptions);
  }

  /**
   * @description Fetches service job details from the API based on the service order ID.
   * @param serviceOrderId 
   * @returns 
   */
  getServiceJobDetails(serviceOrderId: string): Observable<IClaimDetailsServiceJob> {
    const url = getApiUrlForParams(VIVR_SERVICEJOBID_DETAILS, {serviceOrderId});
    return this.httpService.get<IClaimDetailsServiceJob>(url, this.httpOptions);
  }

  /**
   * @description Cancels a service job based on the service order ID.
   * @param serviceOrderId 
   * @returns 
   */
  cancelServiceJob(payLoad: ICancelServiceJob): Observable<IApiGeneralResponse> {
    const { jobNumber } = payLoad;
    const url = getApiUrlForParams(VIVR_SERVICEJOBID_DETAILS_CANCEL, {jobNumber});
    return this.httpService.put<IApiGeneralResponse>(url, payLoad, this.httpOptions);
  }
}